import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { linksApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import UpgradePrompt from '../../components/UpgradePrompt';
import './Links.css';

const LinkCreate = () => {
  const navigate = useNavigate();
  
  const { user } = useAuth();
  const isFreeUser = user?.plan === 'free';
  const [formData, setFormData] = useState({
    name: '',
    destinationUrl: '',
  });
  const [loading, setLoading] = useState(false);
  const [linkCount, setLinkCount] = useState(0);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  const { name, destinationUrl } = formData;
  
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Form validation
    if (!name || !destinationUrl) {
      toast.error('Please fill in all fields');
      return;
    }
    
    // Validate URL format
    try {
      new URL(destinationUrl);
    } catch (err) {
      toast.error('Please enter a valid URL');
      return;
    }
    
    try {
      setLoading(true);
      
      // Create the link via API
      const response = await linksApi.createLink({
        name,
        destination: destinationUrl
      });
      
      if (response.data.status === 'success') {
        toast.success('Affiliate link created successfully');
        navigate('/dashboard/links');
      } else {
        throw new Error('Failed to create link');
      }
    } catch (err) {
      console.error('Error creating link:', err);
      // Detect backend freemium limit block (click/conversion limit)
      const backendMsg = err.response?.data?.message || '';
      if (backendMsg.toLowerCase().includes('click limit')) {
        setShowUpgradePrompt(true);
      } else if (backendMsg.toLowerCase().includes('conversion limit')) {
        setShowUpgradePrompt(true);
      } else {
        toast.error(backendMsg || 'Failed to create link');
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch links to count
    async function fetchLinks() {
      try {
        const response = await linksApi.getLinks();
        if (response.data.status === 'success') {
          setLinkCount(response.data.data.length);
        }
      } catch (err) {
        // ignore for now
      }
    }
    fetchLinks();
  }, []);

  const linkLimitReached = isFreeUser && linkCount >= 3;

  if (linkLimitReached) {
    return (
      <div className="upgrade-message" style={{background:'#fff3cd',border:'1px solid #ffeeba',color:'#856404',padding:'16px',borderRadius:'8px',margin:'16px 0',fontWeight:'500'}}>
        <h2 style={{marginTop:0}}>Link Limit Reached</h2>
        <p style={{marginBottom:0}}>
          Free users can only create up to 3 links.<br/>
          To create more links, please <a href="/upgrade" style={{color:'#007bff',textDecoration:'underline'}}>upgrade to a paid plan</a>.
        </p>
      </div>
    );
  }

  return (
    <div className="form-page">
      {showUpgradePrompt && (
        <UpgradePrompt type="click" onClose={() => setShowUpgradePrompt(false)} />
      )}
      <div className="page-header">
        <h1>Create Affiliate Link</h1>
      </div>
      
      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Link Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={name}
              onChange={handleChange}
              className="form-control"
              placeholder="e.g. Product Homepage"
              required
            />
            <small className="form-text">
              Give your link a memorable name to identify it in your dashboard
            </small>
          </div>
          
          <div className="form-group">
            <label htmlFor="destinationUrl">Destination URL</label>
            <input
              type="url"
              id="destinationUrl"
              name="destinationUrl"
              value={destinationUrl}
              onChange={handleChange}
              className="form-control"
              placeholder="https://example.com/your-landing-page"
              required
            />
            <small className="form-text">
              This is where users will be sent after clicking your affiliate link
            </small>
          </div>
          
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/dashboard/links')}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Link'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LinkCreate;
